import React, { useState } from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text/Text'
import palette from 'core-system/Themes/palette'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import {
  DashboardLocationCard,
  DashboardPeriod,
  DashboardProgramCard,
} from '../dashboardTypes'
import LocationCardsRow from './LocationCardsRow'
import ProgramCardsRow from './ProgramCardsRow'

type OverviewMode = 'site' | 'program'
type SiteGroup = 'country' | 'region'

const SITE_GROUP_OPTIONS: { value: SiteGroup; label: string }[] = [
  { value: 'country', label: 'By Country' },
  { value: 'region', label: 'By Region' },
]

const MODE_OPTIONS: { value: OverviewMode; label: string }[] = [
  { value: 'site', label: 'By Site' },
  { value: 'program', label: 'By Program' },
]

const Section = styled.div`
  margin-bottom: 1.5rem;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  min-width: 0;
`

const Toggle = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem;
  border-radius: 0.5rem;
  background-color: ${palette.grey.grey200};
`

const Option = styled.button<{ $active: boolean }>`
  appearance: none;
  border: none;
  cursor: pointer;
  padding: 0.375rem 0.875rem;
  border-radius: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.25;
  color: ${(props) =>
    props.$active ? palette.text.primary : palette.text.secondary};
  background-color: ${(props) =>
    props.$active ? palette.white : 'transparent'};
  box-shadow: ${(props) =>
    props.$active ? '0 1px 2px rgba(13, 12, 12, 0.08)' : 'none'};

  &:hover {
    color: ${palette.text.primary};
  }

  &:focus-visible {
    outline: 2px solid ${palette.primary.pink500};
    outline-offset: 2px;
  }
`

interface OverviewSectionProps {
  locationCards?: DashboardLocationCard[] | null
  programCards?: DashboardProgramCard[] | null
  locationsLoading?: boolean
  programsLoading?: boolean
  period?: DashboardPeriod
  onLocationClick: (card: DashboardLocationCard) => void
  onProgramClick: (card: DashboardProgramCard) => void
  onAddSite: () => void
  onAddProgram: () => void
}

const OverviewSection = ({
  locationCards = null,
  programCards = null,
  locationsLoading = false,
  programsLoading = false,
  period = 'all',
  onLocationClick,
  onProgramClick,
  onAddSite,
  onAddProgram,
}: OverviewSectionProps) => {
  const [mode, setMode] = useState<OverviewMode>('site')
  const [siteGroup, setSiteGroup] = useState<SiteGroup>('country')
  const isSite = mode === 'site'

  return (
    <Section data-cy='overview-section'>
      <Header>
        <HeaderLeft>
          <Text variant='h3' fontWeight={500}>
            Overview
          </Text>
          <Toggle role='group' aria-label='Overview grouping'>
            {MODE_OPTIONS.map((option) => (
              <Option
                key={option.value}
                type='button'
                $active={mode === option.value}
                aria-pressed={mode === option.value}
                data-cy={`overview-toggle-${option.value}`}
                onClick={() => setMode(option.value)}
              >
                {option.label}
              </Option>
            ))}
          </Toggle>
          {isSite ? (
            <Toggle role='group' aria-label='Site grouping'>
              {SITE_GROUP_OPTIONS.map((option) => (
                <Option
                  key={option.value}
                  type='button'
                  $active={siteGroup === option.value}
                  aria-pressed={siteGroup === option.value}
                  data-cy={`site-group-${option.value}`}
                  onClick={() => setSiteGroup(option.value)}
                >
                  {option.label}
                </Option>
              ))}
            </Toggle>
          ) : null}
        </HeaderLeft>
        <PrimaryButton
          data-cy={isSite ? 'overview-add-site' : 'overview-add-program'}
          onClick={isSite ? onAddSite : onAddProgram}
        >
          {isSite ? '+ Add Site' : '+ Add Program'}
        </PrimaryButton>
      </Header>
      {isSite ? (
        <LocationCardsRow
          cards={locationCards}
          isLoading={locationsLoading}
          groupBy={siteGroup}
          onCardClick={onLocationClick}
        />
      ) : (
        <ProgramCardsRow
          cards={programCards}
          isLoading={programsLoading}
          period={period}
          onCardClick={onProgramClick}
        />
      )}
    </Section>
  )
}

export default React.memo(OverviewSection)
